import React, { useState, useCallback } from "react";
import { formatFileSize } from "../utils/utils";
import { useAuth } from "../../../features/auth/AuthContext";
import DataTable, { RowActionsTray } from "../Components/DataTable";
import { getFileActions } from "../utils/fileActions";
import DeleteModal from "../FileActions/DeleteModal";
import { fileIcons } from "../Icons";
import { format } from "date-fns";
import Filter from "../Components/Filter";
import Header from "../Components/Header";
import Loader from "../Components/Loader";
import GridView from "../Components/GridView";
import Pagination from "../Components/Pagination";
import FloatingAIButton from "../../FloatingAIButton/FloatingAIButton";
import {
  setDrawerState,
  setIsWebAllowed,
  setQueryMetaData,
} from "../../../state/reducers/chatDrawerReducers";
import { useDispatch, useSelector } from "react-redux";
import { useFileActions } from "../hooks/useFileActions";
import { useRepoQuery } from "../hooks/useRepoQuery";
import SelectAll from "../Components/SelectAll";
import { useModal } from "../hooks/useModal";
import FilePreviewModal2 from "../../FilePreview/FilePreviewModal2";
import FileBulkActionBar from "../Components/Mobile/FileBulkActionBar";
import Filelist from "../Components/Mobile/Filelist";

const Bin = () => {
  const [openFilter, setOpenFilter] = useState(false);
  const [activeView, setActiveView] = useState("file");
  const [filters, setFilters] = useState();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const chatDrawerState = useSelector((state) => state?.ChatDrawerState);
  const dispatch = useDispatch();

  const { modal, reqData, openModal, closeModal } = useModal();
  const { token } = useAuth();

  /* ---------------- FILE ACTIONS ---------------- */

  const {
    deleteFile,
    restoreFile,
    openDelete,
    isDeleting,
    isRestoring,
    preview,
  } = useFileActions({
    token,
    openModal,
    closeModal,
  });

  /* ---------------- QUERY ---------------- */

  const { data, isLoading, isFetching } = useRepoQuery({
    token,
    endpoint: "repoview/",
    queryKey: "bin",
    view: "bin",
    page: currentPage,
    search,
    filters,
  });

  const files = data?.files || [];
  const totalCount = data?.count || 0;
  const selectableRows =
    files?.filter((row) => row?.upload_type === "Manual") || [];

  /* ---------------- FILTER ---------------- */

  const handleFilterClick = () => setOpenFilter(true);
  const closeFilterDrawer = () => setOpenFilter(false);

  const handleApplyFilter = (filterObj) => {
    setFilters(filterObj);
    setCurrentPage(1);
    closeFilterDrawer();
  };
  const resetFilter = () => {
    setFilters(null);
    setCurrentPage(1);
    closeFilterDrawer();
  };

  /* ---------------- TABLE ACTIONS ---------------- */

  const handlers = {
    restoreFile,
    openDelete,
  };

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    {
      key: "file_name",
      title: "Name",
      sortable: true,
      render: (val, row) => {
        const FileIcon = fileIcons?.[row?.file_type];
        return (
          <button
            className="border-none bg-transparent flex items-center gap-2 w-full"
            onClick={() => preview(row)}
          >
            <span>{FileIcon && <FileIcon />}</span>
            <span className="text-[#555555] font-normal text-[0.8rem] truncate">
              {row?.file_name}
            </span>
          </button>
        );
      },
    },
    {
      key: "upload_type",
      title: "",
      render: (val, row) => (
        <div className="file-type">
          {val ? val : row?.item_type === "folder" ? "Generated" : ""}
        </div>
      ),
    },
    {
      key: "full_path",
      title: "Source",
      render: (val) => (
        <div className="truncate text-[#555555] text-[0.8rem]">
          {val?.replace(/^Manually uploaded\//i, "")}
        </div>
      ),
    },
    {
      key: "updated_at",
      title: "Deleted On",
      sortable: true,
      render: (val) => (
        <div className="text-[#555555] text-[0.8rem]">
          {format(new Date(val), "dd/MM/yy")}
        </div>
      ),
    },
    {
      key: "file_size",
      title: "Size",
      sortable: true,
      render: (val, row) => (
        <span className="text-[#555555] text-[0.8rem]">
          {formatFileSize(row?.file_size)}
        </span>
      ),
    },
    {
      key: "actions",
      title: "",
      render: (val, row, idx, isHovered) => {
        const rowActions = getFileActions({
          row,
          handlers,
          isBin: true,
        });

        return (
          <div
            style={{
              position: "relative",
              width: 5 * 28 + 10,
              padding: "0 8px",
            }}
          >
            <RowActionsTray
              actions={rowActions}
              row={row}
              visible={isHovered}
            />
          </div>
        );
      },
    },
  ];

  const handleSearchChange = useCallback(
    (input) => {
      if (input === search) return;
      setSearch(input);
      setCurrentPage(1);
    },
    [search],
  );

  return (
    <>
      <Header
        setActiveView={setActiveView}
        activeView={activeView}
        clickFilter={handleFilterClick}
        title="Bin"
        btnLabel={null}
        fileView
        isFilter
        searchParam={search}
        setSearchParam={handleSearchChange}
        searchPlaceholder="Search files"
        justify={window.innerWidth < 650 ? 'justify-end' : null}
      />

      <Filter
        open={openFilter}
        close={closeFilterDrawer}
        updatedOn
        fileType
        appliedFilters={filters}
        applyFilter={handleApplyFilter}
        enableResults={true}
        filterAPI="repoview/"
        view={"bin"}
        onReset={resetFilter}
      />

      <div className="md:shadow-[0_0_12px_#00000014] px-2 md:px-8 py-2 bg-white rounded-[12px] mt-4">
        {isLoading || isRestoring || isFetching ? (
          <Loader />
        ) : activeView === "file" ? (
          <>
            {window.innerWidth < 640 ? (
              <div className="flex flex-col gap-4">
                {selectedKeys?.length > 0 && (
                  <FileBulkActionBar
                    data={files}
                    handleDelete={openDelete}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    handleRestore={restoreFile}
                  />
                )}
                <Filelist
                  data={files}
                  filePreview={preview}
                  handleDelete={openDelete}
                  selectedKeys={selectedKeys}
                  setSelectedKeys={setSelectedKeys}
                  handleRestore={restoreFile}
                  isBin={true}
                />
              </div>
            ) : (
              <>
                <SelectAll
                  selectedKeys={selectedKeys}
                  setSelectedKeys={setSelectedKeys}
                  handleRestore={restoreFile}
                  handleDelete={openDelete}
                  isPermanentDelete={true}
                  selectableRows={selectableRows}
                />

                <DataTable
                  columns={columns}
                  dataSource={files}
                  maxHeight="max-h-90vh"
                  selectedKeys={selectedKeys}
                  setSelectedKeys={setSelectedKeys}
                  checkable={true}
                  rowSelectable={(row) =>
                    row?.upload_type === "Manual" || false
                  }
                  rowKey="id"
                  emptyText="No files found"
                  onRow={(row) => ({
                    onClick: () => {
                      preview(row);
                    },
                  })}
                />
              </>
            )}
          </>
        ) : (
          <GridView
            totalFiles={data?.totalFiles || 0}
            restoreFile={restoreFile}
            handleDelete={openDelete}
            isBin={true}
            files={files}
            previewFile={preview}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalCount={totalCount}
      />

      <DeleteModal
        isBin={true}
        fileName={reqData?.file_name || null}
        open={modal === "delete"}
        close={closeModal}
        loading={isDeleting}
        handleDelete={() =>
          deleteFile(reqData || selectedKeys, "hard", setSelectedKeys)
        }
        selectedKeys={selectedKeys}
      />

      <FilePreviewModal2
        isOpen={modal === "filePreview" ? true : false}
        file={reqData}
        onClose={closeModal}
      />

      <FloatingAIButton
        onClick={() => {
          dispatch(setDrawerState(!chatDrawerState.isOpen));
          dispatch(setIsWebAllowed(true));
          dispatch(setQueryMetaData(null));
        }}
      />
    </>
  );
};

export default Bin;
